import {DetailsList, Pivot, PivotItem, Spinner, Text} from "@fluentui/react";
import Axios from "axios";
import React, {FC, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {
    SqlObjectDefinitionRequestDto,
    SqlObjectDetailsDto,
    SqlObjectDetailsRequestDto,
    SqlObjectMetadataDto,
} from "../API";
import {GetApiError} from "./ShowApiError";

export interface SqlObjectParentIdentity {
    objectType: "table" | "view" | "procedure" | "function" | "user" | "role";
    schemaName?: string;
    objectName: string;
}

interface SqlObjectDetailsPaneProps {
    parent?: SqlObjectParentIdentity;
    metadataCompanyId?: number;
    metadataConnectionString?: string;
    onDefinitionRequested: (request: SqlObjectDefinitionRequestDto) => void;
}

const categories: Array<{key: keyof SqlObjectDetailsDto; titleKey: string}> = [
    {key: "columns", titleKey: "sql_columns"},
    {key: "constraints", titleKey: "sql_constraints"},
    {key: "indexes", titleKey: "sql_indexes"},
    {key: "triggers", titleKey: "sql_triggers"},
    {key: "parameters", titleKey: "sql_parameters"},
    {key: "dependencies", titleKey: "sql_dependencies"},
    {key: "memberships", titleKey: "sql_memberships"},
    {key: "permissions", titleKey: "sql_permissions"},
];

const parentKey = (parent?: SqlObjectParentIdentity) =>
    parent ? `${parent.objectType}\u0000${parent.schemaName ?? ""}\u0000${parent.objectName}` : undefined;

const detailsPaneStyle: React.CSSProperties = {maxHeight: "55vh", overflowY: "auto"};

export const SqlObjectDetailsPane: FC<SqlObjectDetailsPaneProps> = ({
    parent,
    metadataCompanyId,
    metadataConnectionString,
    onDefinitionRequested,
}) => {
    const {t} = useTranslation();
    const requestIdRef = useRef(0);
    const [details, setDetails] = useState<SqlObjectDetailsDto>();
    const [detailsParentKey, setDetailsParentKey] = useState<string>();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>();
    const parentObjectType = parent?.objectType;
    const parentSchemaName = parent?.schemaName ?? "";
    const parentObjectName = parent?.objectName ?? "";
    const selectedParentKey = parentObjectType && parentObjectName
        ? parentKey({objectType: parentObjectType, schemaName: parentSchemaName, objectName: parentObjectName})
        : undefined;
    const activeParentKeyRef = useRef<string>();
    activeParentKeyRef.current = selectedParentKey;

    useEffect(() => {
        const requestId = ++requestIdRef.current;
        setDetails(undefined);
        setDetailsParentKey(undefined);
        setError(undefined);

        if (!parentObjectType || !parentObjectName) {
            setLoading(false);
            return () => {
                requestIdRef.current++;
            };
        }

        setLoading(true);
        const request: SqlObjectDetailsRequestDto = {
            objectType: parentObjectType,
            schemaName: parentSchemaName,
            objectName: parentObjectName,
        };
        Axios.post<SqlObjectDetailsDto>(
            "SqlMetadata/ObjectDetails",
            request,
            {
                params: {
                    additionalIntParameter: metadataCompanyId,
                    additionalStringParameter: metadataConnectionString,
                },
            },
        ).then(
            ({data}) => {
                if (requestId === requestIdRef.current) {
                    setDetails(data);
                    setDetailsParentKey(selectedParentKey);
                    setLoading(false);
                }
            },
            (requestError) => {
                if (requestId === requestIdRef.current) {
                    setError(GetApiError(requestError));
                    setLoading(false);
                }
            },
        );

        return () => {
            requestIdRef.current++;
        };
    }, [selectedParentKey, parentObjectType, parentSchemaName, parentObjectName, metadataCompanyId, metadataConnectionString]);

    const columns = useMemo(() => [
        {key: "name", name: t("sql_object"), fieldName: "objectName", minWidth: 110},
        {key: "type", name: t("sql_object_type"), fieldName: "objectKind", minWidth: 80},
        {key: "details", name: t("sql_details"), fieldName: "details", minWidth: 140},
        {key: "disabled", name: t("sql_disabled"), minWidth: 65, onRender: (item: SqlObjectMetadataDto) => item.isDisabled ? t("yes") : ""},
    ], [t]);

    const requestDefinition = useCallback((item?: SqlObjectMetadataDto) => {
        if (!parentObjectType || !parentObjectName || activeParentKeyRef.current !== selectedParentKey || detailsParentKey !== selectedParentKey || !item?.objectType || !item.objectName) {
            return;
        }

        onDefinitionRequested({
            objectType: item.objectType,
            schemaName: item.schemaName ?? parentSchemaName,
            objectName: item.objectName,
            parentSchemaName,
            parentObjectName,
        });
    }, [detailsParentKey, onDefinitionRequested, parentObjectName, parentObjectType, parentSchemaName, selectedParentKey]);

    if (!parent) {
        return null;
    }

    if (loading) {
        return <div style={detailsPaneStyle}><Spinner label={t("sql_object_details_loading")}/></div>;
    }

    if (error) {
        return <div style={detailsPaneStyle}><Text>{t("sql_object_details_load_failed")}: {error}</Text></div>;
    }

    const visibleDetails = detailsParentKey === selectedParentKey ? details : undefined;
    const populatedCategories = categories.filter(({key}) => visibleDetails?.[key]?.length);
    if (visibleDetails && populatedCategories.length === 0) {
        return <div style={detailsPaneStyle}><Text>{t("sql_object_details_empty")}</Text></div>;
    }

    return <div style={detailsPaneStyle}><Pivot>
        {populatedCategories.map(({key, titleKey}) => <PivotItem headerText={t(titleKey)} key={key}>
            <DetailsList columns={columns} compact items={visibleDetails?.[key] ?? []} onItemInvoked={requestDefinition}/>
        </PivotItem>)}
    </Pivot></div>;
};
